import React, { useState } from 'react';
import { View, StyleSheet, useWindowDimensions, Text, TouchableOpacity } from 'react-native';
import { NavigationContainer, createNavigationContainerRef } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { M01SplashScreen } from './screens/M01SplashScreen';
import { M02LoginScreen } from './screens/M02LoginScreen';
import { M03HomeScreen } from './screens/M03HomeScreen';
import { M04ProductListScreen } from './screens/M04ProductListScreen';
import { M05ProductDetailScreen } from './screens/M05ProductDetailScreen';
import { M06InputCustomerScreen } from './screens/M06InputCustomerScreen';
import { M07ResultScreen } from './screens/M07ResultScreen';
import { M08CompareScreen } from './screens/M08CompareScreen';
import { M09PdfScreen } from './screens/M09PdfScreen';
import { M10SaveCustomerScreen } from './screens/M10SaveCustomerScreen';
import { M11HistoryScreen } from './screens/M11HistoryScreen';
import { M12ProfileScreen } from './screens/M12ProfileScreen';
import { AppBar, BottomNav, LogoIcon, Icon } from './components';
import type { RootStackParamList } from './types/navigation';
import { APP_BAR_HEIGHT, BOTTOM_NAV_HEIGHT, colors, spacing, fontWeights, borderRadius } from './theme';

export type { RootStackParamList } from './types/navigation';

const Stack = createStackNavigator<RootStackParamList>();
const navigationRef = createNavigationContainerRef<RootStackParamList>();

const SIDEBAR_ITEMS = [
  { id: 'M03HomeScreen', label: 'Trang chủ', icon: 'home' },
  { id: 'M04ProductListScreen', label: 'Tính nhanh TLMHBH', icon: 'calculate' },
  { id: 'M11HistoryScreen', label: 'Tài liệu minh họa', icon: 'description' },
  { id: 'M12ProfileScreen', label: 'Hồ sơ Tư vấn', icon: 'person' },
] as const;

const linking = {
  prefixes: typeof window !== 'undefined' ? [window.location.origin] : [],
  config: {
    screens: {
      M01SplashScreen: 'loading',
      M02LoginScreen: 'login',
      M03HomeScreen: 'trang-chu',
      M04ProductListScreen: 'tinh-nhanh',
      M05ProductDetailScreen: 'chi-tiet-san-pham',
      M06InputCustomerScreen: 'tao-minh-hoa',
      M07ResultScreen: 'ket-qua',
      M08CompareScreen: 'so-sanh',
      M09PdfScreen: 'pdf',
      M10SaveCustomerScreen: 'luu-khach-hang',
      M11HistoryScreen: 'tai-lieu-minh-hoa',
      M12ProfileScreen: 'ho-so',
    },
  },
};

export const AppNavigator: React.FC = () => {
  const [currentRoute, setCurrentRoute] = useState<string>('M01SplashScreen');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const { width } = useWindowDimensions();

  const isDesktop = width >= 1024;
  const showHeader = currentRoute !== 'M01SplashScreen' && currentRoute !== 'M02LoginScreen';
  const showFooter = currentRoute !== 'M01SplashScreen' && currentRoute !== 'M02LoginScreen';

  const getActiveTab = (currentScreen: string): string => {
    if (currentScreen === 'M03HomeScreen') return 'M03HomeScreen';
    if (
      [
        'M04ProductListScreen',
        'M05ProductDetailScreen',
        'M06InputCustomerScreen',
        'M07ResultScreen',
        'M08CompareScreen',
        'M09PdfScreen',
        'M10SaveCustomerScreen',
      ].includes(currentScreen)
    ) {
      return 'M04ProductListScreen';
    }
    if (currentScreen === 'M11HistoryScreen') return 'M11HistoryScreen';
    if (currentScreen === 'M12ProfileScreen') return 'M12ProfileScreen';
    return '';
  };

  const getHeaderTitle = (currentScreen: string): string => {
    const titleMap: Record<string, string> = {
      M03HomeScreen: 'Trang chủ',
      M04ProductListScreen: 'Lựa chọn sản phẩm và tạo Tài liệu minh họa bán hàng',
      M05ProductDetailScreen: 'Chi tiết sản phẩm',
      M06InputCustomerScreen: 'Tạo minh họa',
      M07ResultScreen: 'Kết quả minh họa',
      M08CompareScreen: 'So sánh phương án',
      M09PdfScreen: 'Tài liệu PDF',
      M10SaveCustomerScreen: 'Lưu khách hàng',
      M11HistoryScreen: 'Danh sách tài liệu minh họa bán hàng đã tạo',
      M12ProfileScreen: 'Cá nhân & Cấu hình',
    };
    return titleMap[currentScreen] || 'eQuote';
  };

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      onStateChange={(state) => {
        if (state) {
          let route: any = state.routes[state.index];
          while (route.state) {
            const nestedIndex = route.state.index ?? 0;
            route = route.state.routes[nestedIndex];
          }
          setCurrentRoute(route.name);
        }
      }}
    >
      <View style={isDesktop ? styles.desktopShell : styles.shell}>
        {/* Render Sidebar on Desktop only, and only if showHeader is true */}
        {isDesktop && showHeader && (
          <View style={[styles.sidebar, isSidebarCollapsed && styles.sidebarCollapsed]}>
            <View style={[styles.sidebarBrand, isSidebarCollapsed && styles.sidebarBrandCentered]}>
              {isSidebarCollapsed ? (
                <LogoIcon size={36} />
              ) : (
                <>
                  <LogoIcon size={32} />
                  <Text style={styles.brandText}>
                    <Text style={{ color: '#00A651' }}>e</Text>Quote
                  </Text>
                </>
              )}
            </View>

            <View style={styles.sidebarMenu}>
              {SIDEBAR_ITEMS.map((item) => {
                const isActive = getActiveTab(currentRoute) === item.id;
                return (
                  <TouchableOpacity
                    key={item.id}
                    style={[
                      styles.sidebarItem,
                      isActive && styles.sidebarItemActive,
                      isSidebarCollapsed && styles.sidebarItemCentered
                    ]}
                    onPress={() => {
                      if (navigationRef.isReady()) {
                        navigationRef.navigate(item.id as any);
                      }
                    }}
                  >
                    <Icon name={item.icon} size={22} color={isActive ? '#00A651' : '#cbd5e1'} />
                    {!isSidebarCollapsed && (
                      <Text style={[styles.sidebarItemText, isActive && styles.sidebarItemActiveText]}>
                        {item.label}
                      </Text>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Sidebar Expand/Collapse bottom toggle */}
            <TouchableOpacity 
              style={[styles.sidebarToggleBottom, isSidebarCollapsed && styles.sidebarItemCentered]}
              onPress={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              activeOpacity={0.8}
            >
              <Icon name={isSidebarCollapsed ? 'chevron_right' : 'chevron_left'} size={22} color="#cbd5e1" />
              {!isSidebarCollapsed && (
                <Text style={styles.toggleText}>Thu gọn menu</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.sidebarLogout, isSidebarCollapsed && styles.sidebarLogoutCentered]}
              onPress={() => {
                if (navigationRef.isReady()) {
                  navigationRef.navigate('M02LoginScreen');
                }
              }}
            >
              <Icon name="logout" size={22} color="#f87171" />
              {!isSidebarCollapsed && (
                <Text style={styles.logoutText}>Thoát</Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        {/* Main Content Area */}
        <View style={isDesktop ? styles.desktopContent : styles.mobileContentContainer}>
          {/* Header */}
          {isDesktop ? (
            showHeader && (
              <View style={styles.desktopHeader}>
                <Text style={styles.desktopHeaderTitle}>{getHeaderTitle(currentRoute)}</Text>
                
                {/* Desktop Header Actions */}
                <View style={styles.desktopHeaderActions}>
                  {/* Reminders Pill */}
                  <TouchableOpacity style={styles.headerReminderPill} activeOpacity={0.8}>
                    <Icon name="notifications" size={20} color={colors.primary} />
                    <View style={styles.reminderBadgeDot} />
                    <Text style={styles.headerReminderText}>3 Lời nhắc</Text>
                  </TouchableOpacity>

                  {/* Create Illustration Button */}
                  <TouchableOpacity 
                    style={styles.headerCreateBtn} 
                    onPress={() => {
                      if (navigationRef.isReady()) {
                        navigationRef.navigate('M04ProductListScreen');
                      }
                    }}
                    activeOpacity={0.8}
                  >
                    <Icon name="add" size={18} color="#ffffff" />
                    <Text style={styles.headerCreateBtnText}>Tạo minh họa mới</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )
          ) : (
            showHeader && <AppBar />
          )}

          {/* Navigator Container */}
          <View
            style={[
              isDesktop ? styles.desktopMain : styles.content,
              !isDesktop && {
                paddingTop: showHeader ? APP_BAR_HEIGHT : 0,
                paddingBottom: showFooter ? BOTTOM_NAV_HEIGHT : 0,
              }
            ]}
          >
            <Stack.Navigator
              initialRouteName="M01SplashScreen"
              screenOptions={{
                headerShown: false,
                cardStyle: { backgroundColor: '#f5f7fa' },
              }}
            >
              <Stack.Screen name="M01SplashScreen"        component={M01SplashScreen} />
              <Stack.Screen name="M02LoginScreen"         component={M02LoginScreen} />
              <Stack.Screen name="M03HomeScreen"          component={M03HomeScreen} />
              <Stack.Screen name="M04ProductListScreen"   component={M04ProductListScreen} />
              <Stack.Screen name="M05ProductDetailScreen" component={M05ProductDetailScreen} />
              <Stack.Screen name="M06InputCustomerScreen" component={M06InputCustomerScreen} />
              <Stack.Screen name="M07ResultScreen"        component={M07ResultScreen} />
              <Stack.Screen name="M08CompareScreen"       component={M08CompareScreen} />
              <Stack.Screen name="M09PdfScreen"          component={M09PdfScreen} />
              <Stack.Screen name="M10SaveCustomerScreen"  component={M10SaveCustomerScreen} />
              <Stack.Screen name="M11HistoryScreen"       component={M11HistoryScreen} />
              <Stack.Screen name="M12ProfileScreen"       component={M12ProfileScreen} />
            </Stack.Navigator>
          </View>

          {/* Footer (Mobile only) */}
          {!isDesktop && showFooter && <BottomNav />}
        </View>
      </View>
    </NavigationContainer>
  );
};

const styles = StyleSheet.create({
  shell: { 
    flex: 1, 
    flexDirection: 'column', 
    overflow: 'hidden' 
  },
  mobileContentContainer: {
    flex: 1,
    flexDirection: 'column',
    position: 'relative',
  },
  content: { 
    flex: 1, 
    minHeight: 0 
  },

  /* DESKTOP STYLES */
  desktopShell: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#0f172a',
    height: '100vh' as any,
  },
  sidebar: {
    width: 260,
    backgroundColor: '#003366',
    paddingVertical: spacing[6],
    paddingHorizontal: spacing[4],
    justifyContent: 'space-between',
    borderRightWidth: 1,
    borderRightColor: 'rgba(255, 255, 255, 0.05)',
  },
  sidebarBrand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    marginBottom: spacing[8],
    paddingHorizontal: spacing[2],
  },
  brandText: {
    fontSize: 20,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
  sidebarMenu: {
    flex: 1,
    gap: spacing[2],
  },
  sidebarItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    paddingVertical: spacing[3],
    paddingHorizontal: spacing[4],
    borderRadius: borderRadius.DEFAULT,
    backgroundColor: 'transparent',
  },
  sidebarItemActive: {
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  sidebarItemText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#cbd5e1',
  },
  sidebarItemActiveText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
  },
  sidebarLogout: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    paddingVertical: spacing[3],
    paddingHorizontal: spacing[4],
    borderRadius: borderRadius.DEFAULT,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.2)',
  },

  logoutText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#f87171',
  },
  desktopContent: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '#f5f7fa',
  },
  desktopHeader: {
    height: 64,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing[6],
  },
  desktopHeaderTitle: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    flex: 1,
  },
  desktopHeaderActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
  },
  headerReminderPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2],
    borderRadius: borderRadius.full,
    backgroundColor: '#EDF1F5',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    position: 'relative',
  },
  reminderBadgeDot: {
    position: 'absolute',
    top: 6,
    left: 22,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.tertiary,
    borderWidth: 1.5,
    borderColor: '#EDF1F5',
  },
  headerReminderText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.primary,
  },
  headerCreateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2] + 2,
    borderRadius: borderRadius.DEFAULT,
    backgroundColor: colors.secondary,
    shadowColor: colors.secondary,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 3,
  },
  headerCreateBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
    letterSpacing: 0.2,
  },
  /* Sidebar collapsed styles */
  sidebarCollapsed: {
    width: 72,
    paddingHorizontal: spacing[2],
  },
  sidebarBrandCentered: {
    justifyContent: 'center',
    paddingHorizontal: 0,
    gap: 0,
  },
  sidebarItemCentered: {
    justifyContent: 'center',
    paddingHorizontal: 0,
    gap: 0,
  },
  sidebarToggleBottom: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    paddingVertical: spacing[3],
    paddingHorizontal: spacing[4],
    borderRadius: borderRadius.DEFAULT,
    marginBottom: spacing[2],
  },
  toggleText: {
    fontSize: 13,
    color: '#94a3b8',
    fontWeight: fontWeights.medium,
  },
  sidebarLogoutCentered: {
    justifyContent: 'center',
    gap: 0,
    paddingHorizontal: 0,
  },
  desktopMain: {
    flex: 1,
    minHeight: 0,
  },
});
